import type { Part } from "../../../reducers/models/parts";
import type { InventoryResponse } from "../../../reducers/componentSearch";
import type { FlashPCBWebsocketMessage } from "../../websocket";

export interface PartForIdMessage extends FlashPCBWebsocketMessage {
  type: "part";
  part_id: string;
  part: Part | null;
  // Containers and allocations. The server only sends it to admins and
  // engineers, and only for the parts they asked it for.
  inventory: InventoryResponse | null;
}

export interface SetPartsRequest {
  type: "set_parts";
  part_ids: string[];
  inventory_part_ids: string[];
}

export type SendRequest = (request: SetPartsRequest) => void;

function sameIds(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}

/**
 * Shared, reference-counted set of parts the page is following, plus their
 * latest known state. One instance per PartsSubscriptionProvider; every
 * usePart() on the page reads from it. All parts travel over a single
 * websocket (/parts/subscribe) instead of one socket per part, which keeps
 * us well clear of the browsers' ~200-255 concurrent websocket caps.
 */
export class PartsStore {
  private counts = new Map<string, number>();
  private inventoryCounts = new Map<string, number>();
  private parts = new Map<string, Part | null>();
  private inventories = new Map<string, InventoryResponse | null>();
  private listeners = new Set<() => void>();
  private send: SendRequest | null = null;
  private lastSentIds: string[] | null = null;
  private lastSentInventoryIds: string[] | null = null;
  private flushScheduled = false;
  private version = 0;

  // --- useSyncExternalStore interface -------------------------------------

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** undefined = not (yet) loaded; null = server says no such part. */
  getPart = (partId: string | null): Part | null | undefined => {
    if (partId === null) {
      return undefined;
    }
    return this.parts.get(partId);
  };

  /** undefined = not (yet) loaded, or nobody here may see it. */
  getInventory = (partId: string): InventoryResponse | null | undefined => {
    return this.inventories.get(partId);
  };

  /** Changes whenever anything in the store does. */
  getVersion = (): number => {
    return this.version;
  };

  hasFollowers = (): boolean => {
    return this.counts.size > 0;
  };

  // --- following parts -----------------------------------------------------

  /** Start following a part; returns the matching unfollow. */
  follow(partId: string): () => void {
    const current = this.counts.get(partId) ?? 0;
    this.counts.set(partId, current + 1);
    this.notify();
    this.scheduleFlush();
    return () => {
      this.unfollow(partId);
    };
  }

  /**
   * Start following a part along with its containers and allocations;
   * returns the matching unfollow.
   */
  followWithInventory(partId: string): () => void {
    const current = this.inventoryCounts.get(partId) ?? 0;
    this.inventoryCounts.set(partId, current + 1);
    const unfollow = this.follow(partId);
    return () => {
      const remaining = (this.inventoryCounts.get(partId) ?? 1) - 1;
      if (remaining > 0) {
        this.inventoryCounts.set(partId, remaining);
      } else {
        this.inventoryCounts.delete(partId);
      }
      unfollow();
    };
  }

  receive(message: PartForIdMessage): void {
    // Ignore late messages for parts nobody follows any more.
    if (!this.counts.has(message.part_id)) {
      return;
    }
    this.parts.set(message.part_id, message.part);
    if (this.inventoryCounts.has(message.part_id) && message.inventory) {
      this.inventories.set(message.part_id, message.inventory);
    }
    this.notify();
  }

  /**
   * The socket has (re)authenticated. The server keeps no state across
   * connections, so the full set is re-sent.
   */
  connected(send: SendRequest): void {
    this.send = send;
    this.lastSentIds = null;
    this.lastSentInventoryIds = null;
    this.flush();
  }

  disconnected(): void {
    this.send = null;
  }

  // --- internals -----------------------------------------------------------

  private unfollow(partId: string): void {
    const remaining = (this.counts.get(partId) ?? 1) - 1;
    if (remaining > 0) {
      this.counts.set(partId, remaining);
    } else {
      this.counts.delete(partId);
    }
    // The cached state is kept until the next flush, which is when the
    // server is told. Dropping it here would strand a part that is
    // unfollowed and re-followed within one render pass (for example when
    // the part picker closes and the BOM row picks up the part it chose):
    // the set sent to the server would not change, so the server would
    // never send the part again and the row would load forever.
    this.scheduleFlush();
  }

  /**
   * Forget the cached state of anything that is no longer followed. Once a
   * part is left out of a set_parts message the server stops sending it, so
   * a later follower has to start in "loading" and get fresh data, exactly
   * as a per-part socket would.
   */
  private dropUnfollowed(ids: string[], inventoryIds: string[]): void {
    const followed = new Set(ids);
    const followedWithInventory = new Set(inventoryIds);
    let dropped = false;
    [...this.parts.keys()].forEach((partId) => {
      if (!followed.has(partId)) {
        this.parts.delete(partId);
        dropped = true;
      }
    });
    [...this.inventories.keys()].forEach((partId) => {
      if (!followedWithInventory.has(partId)) {
        this.inventories.delete(partId);
        dropped = true;
      }
    });
    if (dropped) {
      this.notify();
    }
  }

  private scheduleFlush(): void {
    // Coalesce the burst of follow()/unfollow() calls from a render pass
    // into one set_parts message.
    if (this.flushScheduled) {
      return;
    }
    this.flushScheduled = true;
    queueMicrotask(() => {
      this.flushScheduled = false;
      this.flush();
    });
  }

  private flush(): void {
    const ids = [...this.counts.keys()].sort();
    const inventoryIds = [...this.inventoryCounts.keys()].sort();
    this.dropUnfollowed(ids, inventoryIds);
    if (this.send === null) {
      return;
    }
    if (
      this.lastSentIds !== null &&
      this.lastSentInventoryIds !== null &&
      sameIds(ids, this.lastSentIds) &&
      sameIds(inventoryIds, this.lastSentInventoryIds)
    ) {
      return;
    }
    this.lastSentIds = ids;
    this.lastSentInventoryIds = inventoryIds;
    this.send({
      type: "set_parts",
      part_ids: ids,
      inventory_part_ids: inventoryIds,
    });
  }

  private notify(): void {
    this.version += 1;
    this.listeners.forEach((listener) => {
      listener();
    });
  }
}
